import { createAction } from '@reduxjs/toolkit';
import {
  GcashApplicationDocumentResponse,
  GcashCreateDocumentRequest,
  GcashCreatePageRequest,
  GcashDocumentPages,
  GcashDocumentsStaticticsResponse,
  GcashDocumentsStatusResponse,
  GcashDocumentTypesResponse,
  GcashExportDocumentRequest,
  GcashExportDocumentsRequest,
  GetPaginatedGcashApplicationDocumentsParams,
  GetPaginatedGcashApplicationDocumentsResponse,
  GetPaginatedGcashApplicationsParams,
  GetPaginatedGcashApplicationsResponse,
  PaginatedGcashActionLogsResponse,
  PaginatedGcashDocumentsByStatusResponse,
  PaginationGcashWithStatusRequest,
  GcashApplicationResponse,
} from 'features/gcash/api/types';
import { GcashDocumentStatuses } from 'features/gcash/enums/gcash-document-statuses.enum';

export const gcashActions = {
  clearState: createAction('[gcash]CLEAR_STATE'),
  getGcashStatisticsRequest: createAction(
    '[gcash]GET_GCASH_STATISTICS_REQUEST'
  ),
  getGcashStatisticsSuccess: createAction<GcashDocumentsStaticticsResponse>(
    '[gcash]GET_GCASH_STATISTICS_SUCCESS'
  ),
  getGcashStatisticsFailure: createAction<string[]>(
    '[gcash]GET_GCASH_STATISTICS_FAILURE'
  ),
  getGcashLogsRequest: createAction<{
    page: number;
    limit: number;
  }>('[gcash]GET_GCASH_LOGS_REQUEST'),
  getGcashLogsSuccess: createAction<PaginatedGcashActionLogsResponse>(
    '[gcash]GET_GCASH_LOGS_SUCCESS'
  ),
  getGcashLogsFailure: createAction<string[]>('[gcash]GET_GCASH_LOGS_FAILURE'),
  getGcashApplicationsRequest:
    createAction<GetPaginatedGcashApplicationsParams>(
      '[gcash]GET_GCASH_APPLICATIONS_REQUEST'
    ),
  getGcashApplicationsSuccess:
    createAction<GetPaginatedGcashApplicationsResponse>(
      '[gcash]GET_GCASH_APPLICATIONS_SUCCESS'
    ),
  getGcashApplicationsFailure: createAction<string[]>(
    '[gcash]GET_GCASH_APPLICATIONS_FAILURE'
  ),
  createGcashApplicationRequest: createAction<{
    fullName: string;
  }>('[gcash]CREATE_GCASH_APPLICATION_REQUEST'),
  createGcashApplicationSuccess: createAction<GcashApplicationResponse>(
    '[gcash]CREATE_GCASH_APPLICATION_SUCCESS'
  ),
  createGcashApplicationFailure: createAction<string[]>(
    '[gcash]CREATE_GCASH_APPLICATION_FAILURE'
  ),
  getGcashApplicationRequest: createAction<string>(
    '[gcash]GET_GCASH_APPLICATION_REQUEST'
  ),
  getGcashApplicationSuccess: createAction<GcashApplicationResponse>(
    '[gcash]GET_GCASH_APPLICATION_SUCCESS'
  ),
  getGcashApplicationFailure: createAction<string[]>(
    '[gcash]GET_GCASH_APPLICATION_FAILURE'
  ),
  getGcashApplicationDocumentsRequest: createAction<
    GetPaginatedGcashApplicationDocumentsParams & { applicationId: string }
  >('[gcash]GET_GCASH_APPLICATION_DOCUMENTS_REQUEST'),
  getGcashApplicationDocumentsSuccess:
    createAction<GetPaginatedGcashApplicationDocumentsResponse>(
      '[gcash]GET_GCASH_APPLICATION_DOCUMENTS_SUCCESS'
    ),
  getGcashApplicationDocumentsFailure: createAction<string[]>(
    '[gcash]GET_GCASH_APPLICATION_DOCUMENTS_FAILURE'
  ),
  createGcashApplicationDocumentRequest: createAction<
    GcashCreateDocumentRequest & { applicationId: string }
  >('[gcash]CREATE_GCASH_APPLICATION_DOCUMENT_REQUEST'),
  createGcashApplicationDocumentSuccess:
    createAction<GcashApplicationDocumentResponse>(
      '[gcash]CREATE_GCASH_APPLICATION_DOCUMENT_SUCCESS'
    ),
  createGcashApplicationDocumentFailure: createAction<string[]>(
    '[gcash]CREATE_GCASH_APPLICATION_DOCUMENT_FAILURE'
  ),
  getGcashDocumentInfoRequest: createAction<{
    applicationId: string;
    documentId: string;
  }>('[gcash]GET_GCASH_DOCUMENT_INFO_REQUEST'),
  getGcashDocumentInfoSuccess: createAction<GcashApplicationDocumentResponse>(
    '[gcash]GET_GCASH_DOCUMENT_INFO_SUCCESS'
  ),
  getGcashDocumentInfoFailure: createAction<string[]>(
    '[gcash]GET_GCASH_DOCUMENT_INFO_FAILURE'
  ),
  exportSingleDocumentRequest: createAction<GcashExportDocumentRequest>(
    '[gcash]EXPORT_SINGLE_DOCUMENT_REQUEST'
  ),
  exportSingleDocumentSuccess: createAction(
    '[gcash]EXPORT_SINGLE_DOCUMENT_SUCCESS'
  ),
  exportSingleDocumentFailure: createAction<string[]>(
    '[gcash]EXPORT_SINGLE_DOCUMENT_FAILURE'
  ),
  createGcashNewPageRequest: createAction<
    GcashCreatePageRequest & {
      applicationId: string;
      documentId: string;
    }
  >('[gcash]CREATE_GCASH_NEW_PAGE_REQUEST'),
  createGcashNewPageSuccess: createAction<GcashDocumentPages>(
    '[gcash]CREATE_GCASH_NEW_PAGE_SUCCESS'
  ),
  createGcashNewPageFailure: createAction<string[]>(
    '[gcash]CREATE_GCASH_NEW_PAGE_FAILURE'
  ),
  changeGcashDocumentStatusRequest: createAction<{
    applicationId: string;
    documentId: string;
    status: GcashDocumentStatuses;
    reason: string;
  }>('[gcash]CHANGE_GCASH_DOCUMENT_STATUS_REQUEST'),
  changeGcashDocumentStatusSuccess:
    createAction<GcashApplicationDocumentResponse>(
      '[gcash]CHANGE_GCASH_DOCUMENT_STATUS_SUCCESS'
    ),
  changeGcashDocumentStatusFailure: createAction<string[]>(
    '[gcash]CHANGE_GCASH_DOCUMENT_STATUS_FAILURE'
  ),
  getGcashDocumentPagesRequest: createAction<{
    applicationId: string;
    documentId: string;
  }>('[gcash]GET_GCASH_DOCUMENT_PAGES_REQUEST'),
  getGcashDocumentPagesSuccess: createAction<GcashDocumentPages>(
    '[gcash]GET_GCASH_DOCUMENT_PAGES_SUCCESS'
  ),
  getGcashDocumentPagesFailure: createAction<string[]>(
    '[gcash]GET_GCASH_DOCUMENT_PAGES_FAILURE'
  ),
  getDocumentsByStatusRequest: createAction<PaginationGcashWithStatusRequest>(
    '[gcash]GET_DOCUMENTS_BY_STATUS_REQUEST'
  ),
  getDocumentsByStatusSuccess:
    createAction<PaginatedGcashDocumentsByStatusResponse>(
      '[gcash]GET_DOCUMENTS_BY_STATUS_SUCCESS'
    ),
  getDocumentsByStatusFailure: createAction<string[]>(
    '[gcash]GET_DOCUMENTS_BY_STATUS_FAILURE'
  ),
  exportApplicationRequest: createAction<string>(
    '[gcash]EXPORT_APPLICATION_REQUEST'
  ),
  exportApplicationSuccess: createAction('[gcash]EXPORT_APPLICATION_SUCCESS'),
  exportApplicationFailure: createAction<string[]>(
    '[gcash]EXPORT_APPLICATION_FAILURE'
  ),
  exportDocumentsByStatusRequest: createAction<GcashExportDocumentsRequest>(
    '[gcash]EXPORT_DOCUMENTS_BY_STATUS_REQUEST'
  ),
  exportDocumentsByStatusSuccess: createAction(
    '[gcash]EXPORT_DOCUMENTS_BY_STATUS_SUCCESS'
  ),
  exportDocumentsByStatusFailure: createAction<string[]>(
    '[gcash]EXPORT_DOCUMENTS_BY_STATUS_FAILURE'
  ),
  //TODO Remove if counter is not needed
  getDocumentLimitCounterStatisticsRequest: createAction(
    '[gcash]GET_DOCUMENT_LIMIT_COUNTER_STATISTICS_REQUEST'
  ),
  getDocumentLimitCounterStatisticsSuccess: createAction<any>(
    '[gcash]GET_DOCUMENT_LIMIT_COUNTER_STATISTICS_SUCCESS'
  ),
  getDocumentLimitCounterStatisticsFailure: createAction<string[]>(
    '[gcash]GET_DOCUMENT_LIMIT_COUNTER_STATISTICS_FAILURE'
  ),
  getGcashDocumentsStatusRequest: createAction<string>(
    '[gcash]GET_GCASH_DOCUMENTS_STATUS_REQUEST'
  ),
  getGcashDocumentsStatusSuccess: createAction<GcashDocumentsStatusResponse>(
    '[gcash]GET_GCASH_DOCUMENTS_STATUS_SUCCESS'
  ),
  getGcashDocumentsStatusFailure: createAction<string[]>(
    '[gcash]GET_GCASH_DOCUMENTS_STATUS_FAILURE'
  ),
  stopDocumentsStatusPolling: createAction(
    '[gcash]STOP_DOCUMENTS_STATUS_POLLING'
  ),
  getGcashDocumentTypesRequest: createAction(
    '[gcash]GET_GCASH_DOCUMENT_TYPES_REQUEST'
  ),
  getGcashDocumentTypesSuccess: createAction<GcashDocumentTypesResponse>(
    '[gcash]GET_GCASH_DOCUMENT_TYPES_SUCCESS'
  ),
  getGcashDocumentTypesFailure: createAction<string[]>(
    '[gcash]GET_GCASH_DOCUMENT_TYPES_FAILURE'
  ),
};
